/**
 * Session Manager for Persistent Authentication
 * 
 * Handles automatic token refresh, session persistence, and biometric re-authentication
 */

import { createClient } from '@/utils/supabase/client';
import { SESSION_CONFIG, shouldRefreshSession, isRememberMeEnabled } from './session-config';
import { authenticateWithBiometric, isBiometricEnrolled } from './biometric';
import { clientLogger } from '@/lib/client-logger';
const log = clientLogger.child('session');

let refreshInterval: NodeJS.Timeout | null = null;
let isRefreshing = false;

/**
 * Initialize session management with automatic refresh
 */
export function initializeSessionManager(): void {
  if (typeof window === 'undefined') return;
  
  // Clear any existing interval
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }

  // Set up periodic session check
  refreshInterval = setInterval(async () => {
    await checkAndRefreshSession();
  }, SESSION_CONFIG.REFRESH_CHECK_INTERVAL);

  // Also check immediately on init
  void checkAndRefreshSession();

  // Listen for visibility change to check session when app comes to foreground
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }
  
  // Listen for online/offline events
  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleOnline);
  }
}

/**
 * Clean up session manager
 */
export function cleanupSessionManager(): void {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
  
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', handleOnline);
  }
}

/**
 * Check session and refresh if needed
 */
async function checkAndRefreshSession(): Promise<void> {
  if (isRefreshing) return;
  
  try {
    isRefreshing = true;
    const supabase = createClient();
    
    // Get current session
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      log.error('Error getting session', { err: error });
      return;
    }

    if (!session) {
      // No session - check if we should prompt biometric re-auth
      await handleNoSession();
      return;
    }

    // Check if session needs refresh
    if (session.expires_at && shouldRefreshSession(session.expires_at)) {
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        log.error('Refresh failed', { err: refreshError });
        // Session expired - try biometric re-auth
        await handleSessionExpired();
      } else if (data.session) {
        // Dispatch custom event for other parts of the app
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('session-refreshed', {
            detail: { session: data.session }
          }));
        }
      }
    }
  } catch (error) {
    log.error('Unexpected error', { err: error });
  } finally {
    isRefreshing = false;
  }
}

/**
 * Handle case when there's no active session
 */
async function handleNoSession(): Promise<void> {
  // Check if user had "Remember Me" enabled
  if (!isRememberMeEnabled()) {
    return; // User explicitly chose not to stay logged in
  }

  // Check if biometric auth is enrolled
  if (!isBiometricEnrolled()) {
    return; // No biometric credentials
  }

  // Try to re-authenticate with biometric
  const userId = await authenticateWithBiometric();
  
  if (userId) {
    // Biometric auth successful - need to establish new session
    // This would require a server endpoint to exchange biometric auth for session
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('biometric-auth-success', {
        detail: { userId }
      }));
    }
  }
}

/**
 * Handle session expiration
 */
async function handleSessionExpired(): Promise<void> {
  // If remember me enabled and biometric enrolled, try to re-auth
  if (isRememberMeEnabled() && isBiometricEnrolled()) {
    await handleNoSession();
  } else {
    // Clear session and redirect to login
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('session-expired'));
    }
  }
}

/**
 * Handle app coming to foreground
 */
function handleVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    void checkAndRefreshSession();
  }
}

/**
 * Handle coming back online
 */
function handleOnline(): void {
  void checkAndRefreshSession();
}

/**
 * Manually trigger session refresh
 */
export async function refreshSession(): Promise<boolean> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      log.error('Manual refresh failed', { err: error });
      return false;
    }
    
    return Boolean(data.session);
  } catch (error) {
    log.error('Manual refresh error', { err: error });
    return false;
  }
}

/**
 * Get current session status
 */
export async function getSessionStatus(): Promise<{
  active: boolean;
  expiresAt: number | null;
  needsRefresh: boolean;
}> {
  try {
    const supabase = createClient();
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { active: false, expiresAt: null, needsRefresh: false };
    }
    
    return {
      active: true,
      expiresAt: session.expires_at || null,
      needsRefresh: session.expires_at ? shouldRefreshSession(session.expires_at) : false,
    };
  } catch (error) {
    log.error('Error checking status', { err: error });
    return { active: false, expiresAt: null, needsRefresh: false };
  }
}
